import React, { useEffect } from 'react';
import { useReadOnly } from '../../../hooks/useReadOnly';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../../../context/FormContext';
import { SignupLayout } from '../../ui/SignupLayout';
import { formatCurrency } from '../../util/formatters';

const CREDIT_CARD_FEE = 0.03;

function maskLast4(last4) {
    if (!last4) return '-';
    return `${'*'.repeat(6)}${last4}`;
}

const ACCOUNT_TYPE_LABELS = {
    checking: 'Checking',
    savings: 'Savings',
};

export function ReviewPayment({ history }) {
    const { formData, setStep } = useFormContext();
    const { isReadOnly } = useReadOnly();

    const paymentToken = history.location?.state?.paymentToken || '';
    const paymentType = history.location?.state?.paymentType || '';
    const billingInfo = history.location?.state?.billingInfo || {};
    const cardMeta = history.location?.state?.cardMeta || {};
    const bankMeta = history.location?.state?.bankMeta || {};
    const isACH = paymentType === 'ach';

    const selectedOption = formData.RateOptions?.find(
        (o) => o.trigger_type === formData.SelectedTriggerType
    );
    const annualPremium = selectedOption?.total_amount_due || formData.SelectedTotalPremium || 0;
    const processingFee = isACH ? 0 : Math.round(annualPremium * CREDIT_CARD_FEE * 100) / 100;
    const totalCharge = annualPremium + processingFee;

    useEffect(() => {
        setStep('paymentReview');
    }, []);

    // If we arrived here without a token send the user back 
    // the token is single-use and can't be reconstructed.
    useEffect(() => {
        if (!isReadOnly && !paymentToken) {
            const route = isACH ? '/signup/payment/bank' : '/signup/payment/card';
            history.replace(route, { billingInfo, paymentType });
        }
    }, []);

    const handleBack = () => {
        // Pass the NON-SENSITIVE details back
        const route = isACH ? '/signup/payment/bank' : '/signup/payment/card';
        history.push(route, { billingInfo, paymentType });
    };

    const handleConfirmPayment = () => {
        if (isReadOnly) {
            history.push('/signup/complete');
            return; // skip validation, API calls, everything
        }
        // Forward the token to the processing screen, which performs authorize -> issue -> capture.
        history.push('/signup/payment/processing', { paymentToken, paymentType });
    };

    return (
        <SignupLayout showProgress={false}>

            <h1 className="signup-heading">Review and submit payment.</h1>
            <p className="signup-section__desc">
                Please verify your payment information before submitting.
            </p>

            <div className="summary-card" style={{ marginBottom: 'var(--space-lg)' }}>
                <p className="summary-card__title">Payment information</p>

                {isACH ? (
                    <>
                        <div className="summary-grid" style={{ marginBottom: 'var(--space-sm)' }}>
                            <div className="summary-row" style={{ marginBottom: 0 }}>
                                <span className="summary-row__label">Payment method</span>
                                <span className="summary-row__value">Bank account</span>
                            </div>
                            <div className="summary-row" style={{ marginBottom: 0 }}>
                                <span className="summary-row__label">Account type</span>
                                <span className="summary-row__value">
                                    {ACCOUNT_TYPE_LABELS[billingInfo.BankAccountAccountType] || '-'}
                                </span>
                            </div>
                        </div>
                        <div className="summary-row">
                            <span className="summary-row__label">Account number</span>
                            <span className="summary-row__value">
                                {maskLast4(bankMeta.last4)}
                            </span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-row__label">Account holder name</span>
                            <span className="summary-row__value">
                                {billingInfo.BankAccountName || bankMeta.name || '-'}
                            </span>
                        </div>
                        <div className="summary-row" style={{ marginBottom: 0 }}>
                            <span className="summary-row__label">Total annual premium</span>
                            <span className="summary-row__value">{formatCurrency(annualPremium)}</span>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="summary-row">
                            <span className="summary-row__label">Cardholder name</span>
                            <span className="summary-row__value">
                                {billingInfo.CreditCardAccountHolderName || '-'}
                            </span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-row__label">Credit card number</span>
                            <span className="summary-row__value">
                                {maskLast4(cardMeta.last4)}
                                {cardMeta.type ? ` (${cardMeta.type})` : ''}
                            </span>
                        </div>
                        <div className="summary-grid" style={{ marginBottom: 0 }}>
                            <div className="summary-row" style={{ marginBottom: 0 }}>
                                <span className="summary-row__label">Total annual premium</span>
                                <span className="summary-row__value">{formatCurrency(annualPremium)}</span>
                            </div>
                            <div className="summary-row" style={{ marginBottom: 0 }}>
                                <span className="summary-row__label">Processing fee (3%)</span>
                                <span className="summary-row__value">{formatCurrency(processingFee)}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <p className="review-auth-text">
                {isACH
                    ? <>By selecting "Confirm payment" you authorize Elevana to charge your bank account <strong>{formatCurrency(totalCharge)}</strong>.</>
                    : <>By selecting "Confirm payment" you authorize Elevana to charge your credit card <strong>{formatCurrency(totalCharge)}</strong>.</>
                }
            </p>

            <div className="signup-nav">
                <button className="btn signup-cta btn--inverse-cta" onClick={handleBack}>
                    <MdArrowBack /> Back
                </button>
                <button className="btn signup-cta" onClick={handleConfirmPayment}>
                    Confirm payment <MdArrowForward />
                </button>
            </div>
        </SignupLayout>
    );
}